import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AdminLayout, navFor } from "@/components/AdminLayout";
import Login from "@/pages/Login";
import NewCharge from "@/pages/NewCharge";
import Charges from "@/pages/Charges";
import Processors from "@/pages/Processors";
import Hotels from "@/pages/Hotels";
import PublicPay, { InvalidLink } from "@/pages/PublicPay";
import SignDocument from "@/pages/SignDocument";
import Users from "@/pages/Users";
import Team from "@/pages/Team";
import Sales from "@/pages/Sales";
import Docs from "@/pages/Docs";
import Airlines from "@/pages/Airlines";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#F5F6F8]" data-testid="auth-loading" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const Allowed = ({ tab, role, roles, children }) => {
  const { user } = useAuth();
  const ok = tab ? user?.permissions?.includes(tab) : roles ? roles.includes(user?.role) : user?.role === role;
  return ok ? children : <Navigate to="/panel" replace />;
};

const Home = () => {
  const { user } = useAuth();
  const first = navFor(user)[0];
  if (!first) return <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="no-access">Tu cuenta aún no tiene secciones asignadas. Contacta al administrador.</div>;
  return <Navigate to={first.to} replace />;
};

const PUBLIC_HOST = process.env.REACT_APP_PUBLIC_ONLY_HOST;
const PUBLIC_ONLY = process.env.REACT_APP_PUBLIC_ONLY === "1" || window.location.host === PUBLIC_HOST;

const PublicApp = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/pago/:token" element={<PublicPay />} />
      <Route path="/firmar/:token" element={<SignDocument />} />
      <Route path="*" element={<InvalidLink />} />
    </Routes>
    <Toaster position="top-right" richColors />
  </BrowserRouter>
);

function App() {
  if (PUBLIC_ONLY) return <PublicApp />;
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/pago/:token" element={<PublicPay />} />
          <Route path="/enlace-no-valido" element={<InvalidLink />} />
          <Route path="/firmar/:token" element={<SignDocument />} />
          <Route path="/panel" element={<Protected><AdminLayout /></Protected>}>
            <Route index element={<Home />} />
            <Route path="nuevo" element={<Allowed tab="nuevo"><NewCharge /></Allowed>} />
            <Route path="cobros" element={<Allowed tab="cobros"><Charges /></Allowed>} />
            <Route path="procesadores" element={<Allowed tab="procesadores"><Processors /></Allowed>} />
            <Route path="plantillas" element={<Allowed tab="plantillas"><Hotels /></Allowed>} />
            <Route path="ventas" element={<Allowed tab="ventas"><Sales /></Allowed>} />
            <Route path="usuarios" element={<Allowed role="admin"><Users /></Allowed>} />
            <Route path="equipo" element={<Allowed role="manager"><Team /></Allowed>} />
            <Route path="aerolineas" element={<Allowed role="admin"><Airlines /></Allowed>} />
            <Route path="docs" element={<Allowed roles={["admin", "manager", "agent"]}><Docs /></Allowed>} />
          </Route>
          <Route path="*" element={<Navigate to="/panel" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
